import React from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import { useMetricsUi, SectionHead } from "../../components/metrics/metricsUi";
import InsightProgress, { ARTIFACT_EXPECTED_SECONDS, ARTIFACT_STEPS } from "./InsightProgress";

/**
 * The Product Artifact's narrative "Insight" — its summary plus the
 * workflow's insight bullets — shown before the Forecasted Units chart.
 * The rest of the artifact (SKU table, Key Issues, Recommendations) renders
 * further down via ProductArtifactSections; both share the same live
 * `artifact` state (see ForecastResults.js, which owns useForecastArtifact).
 * Renders nothing when there's no content yet to show (none/error/empty) —
 * the "Generate"/"Retry" action lives in ProductArtifactSections instead, so
 * it isn't duplicated here.
 */
const ProductArtifactInsight = ({ artifact }) => {
  const ui = useMetricsUi();
  const { colors } = ui;

  if (artifact.status === "pending") {
    return (
      <Box>
        <SectionHead title="Insight" />
        <InsightProgress
          ui={ui}
          startedAt={artifact.startedAt}
          title="Generating the product artifact"
          steps={ARTIFACT_STEPS}
          label="Artifact progress"
          expectedSeconds={ARTIFACT_EXPECTED_SECONDS}
          waitText="Usually ready in about 2 minutes. You can keep working; it fills in on its own."
        />
      </Box>
    );
  }

  if (artifact.status === "loading") {
    return (
      <Box>
        <SectionHead title="Insight" />
        <Box sx={{ ...ui.card, p: "12px 16px" }}>
          <Skeleton variant="text" width="90%" animation="wave" />
          <Skeleton variant="text" width="75%" animation="wave" />
          <Skeleton variant="text" width="60%" animation="wave" />
        </Box>
      </Box>
    );
  }

  if (artifact.status !== "ready") return null;

  const narrative = artifact.narrative || {};
  const insights = narrative.insights || [];
  if (!narrative.summary && !insights.length) return null;

  return (
    <Box>
      <SectionHead title="Insight" />
      <Box sx={{ ...ui.card, p: "12px 16px", textAlign: "left" }}>
        {narrative.summary && (
          <Typography
            sx={{
              fontSize: 13,
              color: colors.textPrimary,
              lineHeight: 1.6,
              textAlign: "left",
              mb: insights.length ? 1 : 0,
            }}
          >
            {narrative.summary}
          </Typography>
        )}
        {insights.length > 0 && (
          <Box component="ul" sx={{ m: 0, pl: 2.5, textAlign: "left" }}>
            {insights.map((insight, i) => (
              <Typography
                key={i}
                component="li"
                sx={{
                  fontSize: 12.5,
                  color: colors.textPrimary,
                  lineHeight: 1.7,
                  textAlign: "left",
                }}
              >
                {insight}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ProductArtifactInsight;
